import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { connect } from 'react-redux';
import * as Yup from 'yup';
import { Formik } from 'formik';
import {
  Box,
  Button,
  Container,
  TextField,
  Typography
} from '@material-ui/core';

import axiosClient from 'config/axios';

import {
  setUser,
  setActiveUserId,
  setActiveUserType,
  setAuthToken,
  getCustomers,
  getProducts,
  getEmployees,
  getVendors,
  getSalesOrders,
  getSalesOrdersAdmin,
  getPurchaseOrders,
  getReturnOrders,
  getExpenses,
  getExpensesAdmin,
  getExpenseTypes,
  getProductGroups,
  getIDGen,
  incrementFailedLogin,
  resetFailedLogin
} from 'redux/actions';
import { single, admin, standard } from 'variables/userAccountTypes';

const mapStateToProps = (state) => ({
  activeUserId: state.activeUserId,
  authToken: state.authToken,
  failedLogin: state.failedLogin
});

const Login = (props) => {

  const navigate = useNavigate();

  const [alert, setAlert] = useState("");

  useEffect(() => {
    if(props.authToken && props.activeUserId) navigate('/app/dashboard', { replace: true });

  }, [props.authToken, props.activeUserId, navigate]);

  const renderAlert = () => {
    switch (alert) {
      case "badUserEmail":
        return (
          <Typography
            align="center"
            color="error"
            variant="body1"
          >
            Incorrect Email
          </Typography>
        )
      case "badPassword":
        return (
          <Typography
            align="center"
            color="error"
            variant="body1"
          >
            Incorrect Password
          </Typography>
        )
      case "attemptsExceed":
        return (
          <Typography
            align="center"
            color="error"
            variant="h5"
          >
            Please Contact Tech Support
          </Typography>
        )
      default:
        return (<React.Fragment></React.Fragment>)
    }
  };

  const authenticateLogin = (values, setSubmitting) => {
    const userCredentials = {
      email: values.email,
      password: values.password
    };

    axiosClient
      .post('/api/users', userCredentials)
      .then(res => {
        if(res.data.status === "loggedIn") {
          
          props.setUser(res.data.body);
          props.setActiveUserId(res.data.id);
          props.setActiveUserType(res.data.body.acc_type);
          props.setAuthToken(res.data.token);
          props.resetFailedLogin();

          props.getIDGen();
          props.getCustomers();
          props.getProducts();
          if(res.data.body.acc_type === single) {
            props.getEmployees();
            props.getVendors();
            props.getPurchaseOrders();
            props.getReturnOrders();
            props.getProductGroups();
            props.getExpenseTypes();

            props.getSalesOrders();
            props.getExpenses();
          }
          else if(res.data.body.acc_type === admin) {
            props.getEmployees();
            props.getVendors();
            props.getPurchaseOrders();
            props.getReturnOrders();
            props.getProductGroups();
            props.getExpenseTypes();

            props.getSalesOrdersAdmin();
            props.getExpensesAdmin();
          }
          else if(res.data.body.acc_type === standard) {
            props.getSalesOrders();
            props.getExpenses();
          }
          
          navigate('/app/dashboard', { replace: true });
        }
        else {
          if(props.failedLogin.failedLoginAttempts >= 4) setAlert("attemptsExceed");
          else setAlert(res.data.status);
          
          setSubmitting(false);
          props.incrementFailedLogin();
        }
      })
      .catch(err => console.log(err))
  };

  return (
    <React.Fragment>
      <Helmet>
        <title>Login | Duot One</title>
      </Helmet>
      <Box
        sx={{
          backgroundColor: 'background.default',
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          justifyContent: 'center'
        }}
      >
        <Container maxWidth="sm">
          <Formik
            initialValues={{
              email: '',
              password: ''
            }}
            validationSchema={Yup.object().shape({
              email: Yup.string().email('Must be a valid email').max(100).required('Email is required'),
              password: Yup.string().max(50).required('Password is required')
            })}
            onSubmit={(values, {setSubmitting, resetForm}) => {
              authenticateLogin(values, setSubmitting)
            }}
          >
            {({
              errors,
              handleBlur,
              handleChange,
              handleSubmit,
              isSubmitting,
              touched,
              values
            }) => (
              <form onSubmit={handleSubmit}>
                <Box sx={{ mb: 3 }}>
                  <Typography
                    color="textPrimary"
                    variant="h2"
                  >
                    Sign in
                  </Typography>
                  <Typography
                    color="textSecondary"
                    gutterBottom
                    variant="body2"
                  >
                    Sign in to the Duot One System
                  </Typography>
                </Box>
                <Box
                  sx={{
                    pb: 1,
                    pt: 3
                  }}
                >
                  <Typography
                    align="center"
                    color="textSecondary"
                    variant="body1"
                  >
                    login with email address and password
                  </Typography>
                  {renderAlert()}
                </Box>
                <TextField
                  error={Boolean(touched.email && errors.email)}
                  fullWidth
                  helperText={touched.email && errors.email}
                  label="Email Address"
                  margin="normal"
                  name="email"
                  onBlur={handleBlur}
                  onChange={handleChange}
                  type="email"
                  value={values.email}
                  variant="outlined"
                />
                <TextField
                  error={Boolean(touched.password && errors.password)}
                  fullWidth
                  helperText={touched.password && errors.password}
                  label="Password"
                  margin="normal"
                  name="password"
                  onBlur={handleBlur}
                  onChange={handleChange}
                  type="password"
                  value={values.password}
                  variant="outlined"
                />
                <Box sx={{ py: 2 }}>
                  <Button
                    color="primary"
                    disabled={isSubmitting || props.failedLogin.failedLoginAttempts >= 5}
                    fullWidth
                    size="large"
                    type="submit"
                    variant="contained"
                  >
                    Sign in now
                  </Button>
                </Box>
              </form>
            )}
          </Formik>
        </Container>
      </Box>
    </React.Fragment>
  )
};

export default connect (mapStateToProps, {
  setUser,
  setActiveUserId,
  setActiveUserType,
  setAuthToken,
  getCustomers,
  getProducts,
  getEmployees,
  getVendors,
  getSalesOrders,
  getSalesOrdersAdmin,
  getPurchaseOrders,
  getReturnOrders,
  getExpenses,
  getExpensesAdmin,
  getExpenseTypes,
  getProductGroups,
  getIDGen,
  incrementFailedLogin,
  resetFailedLogin
}) (Login);
